/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API_BASE, getToken } from "../lib/apiClient";
import { useMatch } from "../lib/MatchContext";
import RequireMatch from "../components/RequireMatch";
import { Map as MapIcon, Users2, ShieldAlert, Trash2, Upload, Pencil, Check, X, FileText, Image as ImageIcon, Search } from "lucide-react";
import { fmtDateTimeIST } from "../lib/formatDateTime";
import ReorderControls from "../components/ReorderControls";
import PhotoPreview from "../components/PhotoPreview";
import PersonReferencesDrawer from "../components/PersonReferencesDrawer";

const SECTION_META = {
  maps:      { key: "maps",      label: "Maps",                icon: MapIcon,     lead: "Stadium Maps",         blurb: "Uploaded stadium plans available at a glance." },
  persons:   { key: "persons",   label: "Responsible Persons", icon: Users2,      lead: "Responsible Persons",  blurb: "Everyone with a role — click any name to see where they're tagged across live items." },
  emergency: { key: "emergency", label: "Important Documents", icon: ShieldAlert, lead: "Important Documents", blurb: "Emergency SOP documents, checklists and any critical files attached to this match." },
};
const URL_TO_KEY = {
  "maps": "maps",
  "responsible-persons": "persons",
  "emergency-plans": "emergency",
};

function fileUrl(path) {
  return `${API_BASE}/files/${encodeURIComponent(path).replace(/%2F/g, "/")}?auth=${getToken()}`;
}

export default function CriticalInfoPage() {
  const { section: urlSection } = useParams();
  const activeKey = URL_TO_KEY[urlSection] || "maps";
  const meta = SECTION_META[activeKey];

  const { selectedMatchId } = useMatch();
  const DEMO = selectedMatchId;

  // Maps
  const [maps, setMaps] = useState([]);
  const [mapName, setMapName] = useState("");
  const [mapFile, setMapFile] = useState(null);

  // Contacts / Responsible Persons
  const [contacts, setContacts] = useState([]);
  const [contactCategories, setContactCategories] = useState([]); // ordered category list
  const [contactForm, setContactForm] = useState({ category: "", dept_designation: "", name: "", contact_no: "" });
  const [newCatMode, setNewCatMode] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactEditForm, setContactEditForm] = useState({});
  const [editRowNewCat, setEditRowNewCat] = useState(false);
  const [drawerPerson, setDrawerPerson] = useState(null);
  const [personSearch, setPersonSearch] = useState("");

  // Emergency
  const [plans, setPlans] = useState([]);
  const [planHeading, setPlanHeading] = useState("");
  const [planFile, setPlanFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    if (!DEMO) return;
    const [{ data: m }, { data: c }, { data: p }, { data: cats }] = await Promise.all([
      api.get(`/matches/${DEMO}/maps`),
      api.get(`/matches/${DEMO}/contacts`),
      api.get(`/matches/${DEMO}/emergency-plans`),
      api.get(`/matches/${DEMO}/contact-categories`),
    ]);
    setMaps(m); setContacts(c); setPlans(p);
    setContactCategories(cats.categories || []);
  };

  const moveCategory = async (visibleIdx, delta, visibleOrder) => {
    // visibleOrder is the on-screen sequence of persisted categories (empty ones hidden).
    // We reorder within that, then rebuild the full persisted list = new visible order
    // followed by any hidden (unused) categories we should preserve.
    const nextVisible = [...visibleOrder];
    const j = visibleIdx + delta;
    if (j < 0 || j >= nextVisible.length) return;
    [nextVisible[visibleIdx], nextVisible[j]] = [nextVisible[j], nextVisible[visibleIdx]];
    const hidden = contactCategories.filter((c) => !visibleOrder.includes(c));
    const next = [...nextVisible, ...hidden];
    setContactCategories(next); // optimistic
    try {
      await api.post(`/matches/${DEMO}/contact-categories/reorder`, { order: next });
    } catch (e) {
      alert(e.response?.data?.detail || "Could not reorder categories.");
      load();
    }
  };
  useEffect(() => { load(); }, [DEMO]);

  const uploadMap = async (e) => {
    e.preventDefault();
    if (!mapFile || !mapName.trim()) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", mapFile);
      await api.post(`/matches/${DEMO}/maps?name=${encodeURIComponent(mapName)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMapName(""); setMapFile(null); load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const removeMap = async (id) => {
    if (!window.confirm("Delete this map?")) return;
    await api.delete(`/maps/${id}`); load();
  };

  const addContact = async (e) => {
    e.preventDefault();
    const payload = {
      category: (contactForm.category || "").trim(),
      dept_designation: (contactForm.dept_designation || "").trim(),
      name: contactForm.name,
      contact_no: contactForm.contact_no,
    };
    await api.post(`/matches/${DEMO}/contacts`, payload);
    setContactForm({ category: payload.category, dept_designation: "", name: "", contact_no: "" });
    setNewCatMode(false);
    load();
  };
  const saveContact = async (id) => {
    await api.patch(`/contacts/${id}`, contactEditForm);
    setEditingContact(null); load();
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Delete this person?")) return;
    await api.delete(`/contacts/${id}`); load();
  };
  const move = async (personId, delta, categoryContacts) => {
    // Swap the person with the previous/next person **inside the same category**.
    // Prior implementation used the global index which broke when categories were
    // interleaved in the global order (user clicked "up" and nothing visibly moved
    // because the swap happened against a different-category neighbour).
    const pos = categoryContacts.findIndex((c) => c.id === personId);
    if (pos < 0) return;
    const j = pos + delta;
    if (j < 0 || j >= categoryContacts.length) return;
    const partnerId = categoryContacts[j].id;
    const next = [...contacts];
    const a = next.findIndex((c) => c.id === personId);
    const b = next.findIndex((c) => c.id === partnerId);
    if (a < 0 || b < 0) return;
    [next[a], next[b]] = [next[b], next[a]];
    setContacts(next);
    await api.post(`/matches/${DEMO}/contacts/reorder`, { ids: next.map((c) => c.id) });
  };

  const uploadPlan = async (e) => {
    e.preventDefault();
    if (!planFile || !planHeading.trim()) return;
    setBusy(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", planFile);
      await api.post(`/matches/${DEMO}/emergency-plans?heading=${encodeURIComponent(planHeading)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPlanHeading(""); setPlanFile(null); load();
    } catch (e2) { setErr(e2.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  const removePlan = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/emergency-plans/${id}`); load();
  };

  if (!selectedMatchId) return <RequireMatch />;

  const Icon = meta.icon;
  const planFilePreviewIsImage = planFile && planFile.type.startsWith("image/");

  return (
    <div className="p-6 md:p-10 pb-20" data-testid={`page-critical-${activeKey}`}>
      <div className="border-b border-cmdBorder pb-6 mb-6 flex items-start gap-4">
        <div className="border border-cmdBorder p-3">
          <Icon className="w-6 h-6" strokeWidth={1.5} />
        </div>
        <div>
          <div className="mc-label">Critical Information</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">{meta.lead}</h1>
          <p className="text-zinc-400 mt-2 text-sm max-w-2xl">{meta.blurb}</p>
        </div>
      </div>

      {err && <div className="mb-4 text-incidentRed text-sm font-mono-live">{err}</div>}

      {/* ---- MAPS ---- */}
      {activeKey === "maps" && (
        <div>
          <form onSubmit={uploadMap} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-start" data-testid="map-form">
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Map name (e.g., North Pavilion Layout)"
              required
              value={mapName}
              onChange={(e) => setMapName(e.target.value)}
              data-testid="map-form-name"
            />
            <div className="space-y-2">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                required
                onChange={(e) => setMapFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="map-form-file"
              />
              {mapFile && <PhotoPreview files={[mapFile]} onRemove={() => setMapFile(null)} testidPrefix="map-preview" />}
            </div>
            <button type="submit" disabled={busy || !mapFile} className="bg-white text-black py-2 font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-60" data-testid="map-form-submit">
              <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload Map"}
            </button>
          </form>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {maps.map((m) => (
              <div key={m.id} className="mc-panel p-3" data-testid={`map-card-${m.id}`}>
                <a href={fileUrl(m.path)} target="_blank" rel="noreferrer" className="block">
                  <img src={fileUrl(m.path)} alt={m.name} className="w-full h-48 object-cover border border-cmdBorder" />
                </a>
                <div className="flex items-start justify-between mt-3 gap-3">
                  <div>
                    <div className="font-bold text-sm">{m.name}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{fmtDateTimeIST(m.uploaded_at)} · {m.uploaded_by}</div>
                  </div>
                  <button onClick={() => removeMap(m.id)} className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed p-1.5 text-xs" data-testid={`map-del-${m.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            ))}
            {maps.length === 0 && <div className="col-span-full text-center text-zinc-500 py-10 mc-panel">No maps uploaded yet.</div>}
          </div>
        </div>
      )}

      {/* ---- RESPONSIBLE PERSONS (was Important Contacts) ---- */}
      {activeKey === "persons" && (
        <div>
          <div className="flex items-center justify-end mb-3">
            <div className="relative w-full max-w-sm" data-testid="persons-search-wrap">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                type="text"
                value={personSearch}
                onChange={(e) => setPersonSearch(e.target.value)}
                placeholder="Search by name, category, or number…"
                className="w-full bg-black border border-cmdBorder pl-7 pr-8 py-1.5 text-sm outline-none focus:border-white/40"
                data-testid="persons-search"
              />
              {personSearch && (
                <button
                  type="button"
                  onClick={() => setPersonSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="persons-search-clear"
                  title="Clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </div>
          {(() => {
            // Category options for the Add form dropdown come from the current directory
            const catOptions = Array.from(
              new Set(contacts.map((c) => (c.category || "").trim()).filter(Boolean))
            ).sort((a, b) => a.localeCompare(b));
            return (
          <form onSubmit={addContact} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="contact-form">
            {newCatMode ? (
              <div className="flex gap-1">
                <input
                  className="bg-black border border-cmdBorder p-2 text-sm flex-1 min-w-0"
                  placeholder="New category"
                  autoFocus
                  value={contactForm.category}
                  onChange={(e) => setContactForm({ ...contactForm, category: e.target.value })}
                  data-testid="contact-form-cat-new"
                />
                <button
                  type="button"
                  onClick={() => { setNewCatMode(false); setContactForm({ ...contactForm, category: "" }); }}
                  className="border border-cmdBorder hover:border-white px-2 text-xs"
                  title="Back to list"
                  data-testid="contact-form-cat-cancel"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <select
                className="bg-black border border-cmdBorder p-2 text-sm"
                value={catOptions.includes(contactForm.category) ? contactForm.category : ""}
                onChange={(e) => {
                  if (e.target.value === "__new__") {
                    setNewCatMode(true);
                    setContactForm({ ...contactForm, category: "" });
                  } else {
                    setContactForm({ ...contactForm, category: e.target.value });
                  }
                }}
                data-testid="contact-form-cat-select"
              >
                <option value="">Category (optional)…</option>
                {catOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__new__">+ New category…</option>
              </select>
            )}
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Designation"
              value={contactForm.dept_designation}
              onChange={(e) => setContactForm({ ...contactForm, dept_designation: e.target.value })}
              data-testid="contact-form-designation"
            />
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Name"
              required
              value={contactForm.name}
              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
              data-testid="contact-form-name"
            />
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Contact number"
              required
              value={contactForm.contact_no}
              onChange={(e) => setContactForm({ ...contactForm, contact_no: e.target.value })}
              data-testid="contact-form-no"
            />
            <button type="submit" className="bg-readyGreen text-white font-bold uppercase tracking-widest text-xs" data-testid="contact-form-submit">
              + Add Person
            </button>
          </form>
            );
          })()}

          <div className="mc-panel overflow-x-auto">
            {(() => {
              const q = personSearch.trim().toLowerCase();
              const filtered = q
                ? contacts.filter((c) =>
                    [c.name, c.category, c.dept_designation, c.contact_no]
                      .filter(Boolean)
                      .some((v) => v.toLowerCase().includes(q))
                  )
                : contacts;
              // Group by CATEGORY (independent from Designation)
              const groups = {};
              for (const c of filtered) {
                const key = (c.category || "").trim() || "Uncategorised";
                if (!groups[key]) groups[key] = [];
                groups[key].push(c);
              }
              // Order: persisted contactCategories first (only those with persons),
              // then any leftover buckets (incl. Uncategorised) alphabetically.
              const persistedOrder = contactCategories.filter((c) => groups[c]);
              const leftovers = Object.keys(groups)
                .filter((k) => !persistedOrder.includes(k))
                .sort((a, b) => {
                  if (a === "Uncategorised") return 1;
                  if (b === "Uncategorised") return -1;
                  return a.localeCompare(b);
                });
              const ordered = [...persistedOrder, ...leftovers];
              if (filtered.length === 0) {
                return (
                  <div className="text-center text-zinc-500 py-8">
                    {q ? `No persons match "${personSearch}".` : "No responsible persons added yet."}
                  </div>
                );
              }
              const canReorderCats = !q;
              return ordered.map((cat) => {
                const visibleIdx = persistedOrder.indexOf(cat);
                const isPersisted = visibleIdx >= 0;
                return (
                <div key={cat} className="border-b border-cmdBorder last:border-b-0" data-testid={`persons-cat-${cat.replace(/\s+/g, "-")}`}>
                  <div className="px-4 py-2 bg-white/[0.03] flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="mc-label">{cat}</span>
                      <span className="font-mono-live text-[10px] text-zinc-500">({groups[cat].length})</span>
                    </div>
                    {canReorderCats && isPersisted && persistedOrder.length > 1 && (
                      <ReorderControls
                        onUp={() => moveCategory(visibleIdx, -1, persistedOrder)}
                        onDown={() => moveCategory(visibleIdx, 1, persistedOrder)}
                        canUp={visibleIdx > 0}
                        canDown={visibleIdx < persistedOrder.length - 1}
                        testidPrefix={`persons-cat-reorder-${cat.replace(/\s+/g, "-")}`}
                      />
                    )}
                  </div>
                  <table className="w-full mc-table">
                    <tbody>
                      {groups[cat].map((c) => {
                        const idx = contacts.findIndex((x) => x.id === c.id);
                        const posInCat = groups[cat].findIndex((x) => x.id === c.id);
                        const canReorder = !q;
                        return (
                          <tr key={c.id} data-testid={`contact-row-${c.id}`}>
                            <td style={{ width: 60 }}>
                              <ReorderControls
                                onUp={() => move(c.id, -1, groups[cat])}
                                onDown={() => move(c.id, 1, groups[cat])}
                                canUp={canReorder && posInCat > 0}
                                canDown={canReorder && posInCat < groups[cat].length - 1}
                                testidPrefix={`contact-reorder-${c.id}`}
                              />
                            </td>
                            {editingContact === c.id ? (
                              <>
                                <td>
                                  <div className="space-y-1">
                                    {editRowNewCat ? (
                                      <div className="flex gap-1">
                                        <input
                                          className="bg-black border border-cmdBorder p-1.5 text-xs w-full min-w-0"
                                          placeholder="New category"
                                          autoFocus
                                          value={contactEditForm.category ?? ""}
                                          onChange={(e) => setContactEditForm({ ...contactEditForm, category: e.target.value })}
                                          data-testid={`contact-edit-cat-new-${c.id}`}
                                        />
                                        <button
                                          type="button"
                                          onClick={() => { setEditRowNewCat(false); setContactEditForm({ ...contactEditForm, category: "" }); }}
                                          className="border border-cmdBorder hover:border-white px-1.5 text-xs"
                                        >
                                          <X className="w-3 h-3" />
                                        </button>
                                      </div>
                                    ) : (() => {
                                      const catOptions = Array.from(new Set(contacts.map((x) => (x.category || "").trim()).filter(Boolean))).sort((a,b) => a.localeCompare(b));
                                      const cur = contactEditForm.category ?? "";
                                      return (
                                        <select
                                          className="bg-black border border-cmdBorder p-1.5 text-xs w-full"
                                          value={catOptions.includes(cur) ? cur : ""}
                                          onChange={(e) => {
                                            if (e.target.value === "__new__") {
                                              setEditRowNewCat(true);
                                              setContactEditForm({ ...contactEditForm, category: "" });
                                            } else {
                                              setContactEditForm({ ...contactEditForm, category: e.target.value });
                                            }
                                          }}
                                          data-testid={`contact-edit-cat-select-${c.id}`}
                                        >
                                          <option value="">Category…</option>
                                          {catOptions.map((cc) => <option key={cc} value={cc}>{cc}</option>)}
                                          <option value="__new__">+ New category…</option>
                                        </select>
                                      );
                                    })()}
                                    <input
                                      className="bg-black border border-cmdBorder p-1.5 text-xs w-full"
                                      value={contactEditForm.dept_designation ?? ""}
                                      onChange={(e) => setContactEditForm({ ...contactEditForm, dept_designation: e.target.value })}
                                      placeholder="Designation"
                                      data-testid={`contact-edit-designation-${c.id}`}
                                    />
                                  </div>
                                </td>
                                <td><input className="bg-black border border-cmdBorder p-1.5 text-xs w-full" value={contactEditForm.name ?? ""} onChange={(e) => setContactEditForm({ ...contactEditForm, name: e.target.value })} /></td>
                                <td><input className="bg-black border border-cmdBorder p-1.5 text-xs w-full" value={contactEditForm.contact_no ?? ""} onChange={(e) => setContactEditForm({ ...contactEditForm, contact_no: e.target.value })} /></td>
                                <td className="text-right">
                                  <div className="inline-flex gap-2 justify-end">
                                    <button onClick={() => saveContact(c.id)} className="border border-readyGreen text-readyGreen hover:bg-readyGreen hover:text-white px-2 py-1 text-xs"><Check className="w-3 h-3" /></button>
                                    <button onClick={() => { setEditingContact(null); setEditRowNewCat(false); }} className="border border-cmdBorder hover:border-white px-2 py-1 text-xs"><X className="w-3 h-3" /></button>
                                  </div>
                                </td>
                              </>
                            ) : (
                              <>
                                <td className="w-56 text-zinc-400 text-xs">{c.dept_designation || <span className="text-zinc-600">—</span>}</td>
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => setDrawerPerson(c)}
                                    className="text-white underline decoration-dotted underline-offset-4 hover:text-readyGreen"
                                    title="See where this person is tagged"
                                    data-testid={`contact-open-${c.id}`}
                                  >
                                    {c.name}
                                  </button>
                                </td>
                                <td className="font-mono-live text-white w-40">
                                  <a href={`tel:${c.contact_no}`} className="hover:underline">{c.contact_no}</a>
                                </td>
                                <td className="text-right w-32">
                                  <div className="inline-flex gap-2 justify-end">
                                    <button
                                      onClick={() => { setEditingContact(c.id); setEditRowNewCat(false); setContactEditForm({ category: c.category || "", dept_designation: c.dept_designation, name: c.name, contact_no: c.contact_no }); }}
                                      className="border border-cmdBorder hover:border-white px-2 py-1 text-xs"
                                      data-testid={`contact-edit-${c.id}`}
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => deleteContact(c.id)} className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed px-2 py-1 text-xs" data-testid={`contact-del-${c.id}`}>
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              });
            })()}
          </div>
          {drawerPerson && (
            <PersonReferencesDrawer
              matchId={DEMO}
              person={drawerPerson}
              onClose={() => setDrawerPerson(null)}
            />
          )}
        </div>
      )}

      {/* ---- EMERGENCY PLAN ---- */}
      {activeKey === "emergency" && (
        <div>
          <form onSubmit={uploadPlan} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3 items-start" data-testid="plan-form">
            <input
              className="bg-black border border-cmdBorder p-2 text-sm"
              placeholder="Heading (e.g., Evacuation Plan – North Pavilion)"
              required
              value={planHeading}
              onChange={(e) => setPlanHeading(e.target.value)}
              data-testid="plan-form-heading"
            />
            <div className="space-y-2">
              <input
                type="file"
                accept="application/pdf,image/jpeg,image/png,image/webp"
                required
                onChange={(e) => setPlanFile(e.target.files?.[0] || null)}
                className="text-xs"
                data-testid="plan-form-file"
              />
              {planFile && (
                <div>
                  {planFilePreviewIsImage ? (
                    <PhotoPreview files={[planFile]} onRemove={() => setPlanFile(null)} testidPrefix="plan-preview" />
                  ) : (
                    <div className="flex items-center gap-2 border border-cmdBorder p-2 text-xs" data-testid="plan-preview-pdf">
                      <FileText className="w-4 h-4" />
                      <span className="font-mono-live truncate flex-1">{planFile.name}</span>
                      <button type="button" onClick={() => setPlanFile(null)} className="text-zinc-500 hover:text-incidentRed"><X className="w-3 h-3" /></button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <button type="submit" disabled={busy || !planFile} className="bg-white text-black py-2 font-bold uppercase tracking-widest text-xs flex items-center justify-center gap-2 disabled:opacity-60" data-testid="plan-form-submit">
              <Upload className="w-3 h-3" /> {busy ? "Uploading…" : "Upload Document"}
            </button>
          </form>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {plans.map((p) => {
              const isPdf = (p.mime || "").includes("pdf") || (p.filename || "").toLowerCase().endsWith(".pdf");
              return (
                <div key={p.id} className="mc-panel p-4 flex items-start gap-4" data-testid={`plan-card-${p.id}`}>
                  <div className="border border-cmdBorder p-3">
                    {isPdf ? <FileText className="w-8 h-8" strokeWidth={1.4} /> : <ImageIcon className="w-8 h-8" strokeWidth={1.4} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold">{p.heading}</div>
                    <div className="text-xs text-zinc-500 truncate mt-0.5">{p.filename}</div>
                    <div className="text-[10px] text-zinc-500 uppercase tracking-widest mt-1">{fmtDateTimeIST(p.uploaded_at)} · {p.uploaded_by}</div>
                    <div className="mt-3 flex gap-2 flex-wrap">
                      <a href={fileUrl(p.path)} target="_blank" rel="noreferrer" className="border border-cmdBorder hover:border-white px-3 py-1.5 text-xs uppercase tracking-widest" data-testid={`plan-open-${p.id}`}>Open</a>
                      <a href={fileUrl(p.path)} download={p.filename} className="border border-cmdBorder hover:border-white px-3 py-1.5 text-xs uppercase tracking-widest">Download</a>
                      <button onClick={() => removePlan(p.id)} className="border border-cmdBorder hover:border-incidentRed hover:text-incidentRed p-1.5" data-testid={`plan-del-${p.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
            {plans.length === 0 && <div className="col-span-full text-center text-zinc-500 py-10 mc-panel">No emergency documents uploaded yet.</div>}
          </div>
        </div>
      )}
    </div>
  );
}
